import { Grid, makeStyles, useTheme } from '@material-ui/core';
import React, { useMemo, useState } from 'react';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import clsx from 'clsx';
import ListItemText from '@material-ui/core/ListItemText';
import IconButton from '@material-ui/core/IconButton';
import VisibilityIcon from '@material-ui/icons/Visibility';
import FilterIcon from '@material-ui/icons/FilterList';
import Button from '@material-ui/core/Button';
import { useTranslation } from 'react-i18next';
import { MdDelete } from 'react-icons/md';
import CircularProgress from '@material-ui/core/CircularProgress';
import AppLoading from '../../components/app/AppLoading';
import { useNotification, useNotifications } from '../../models/notifications';
import { useDatetimeFormat } from '../../utils';
import AppPagination from '../../components/app/AppPagination';

const useStyles = makeStyles((theme) => {
  return {
    list: {
      width: '100%',
    },
    listItem: {
      marginBottom: '1px',
    },
    listItemOdd: {
      backgroundColor: theme.palette.alternate.light,
    },
    listItemEven: {
      backgroundColor: 'white',
    },
    listItemOddActive: {
      backgroundColor: '#ffeee0',
    },
    listItemEvenActive: {
      backgroundColor: '#ffe5ce',
    },
  };
});

const NotificationItem = ({ notification, i }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const dateFormat = useDatetimeFormat();

  const {
    readNotification,
    notificationIsUpdating,
    deleteNotification,
    notificationIsDeleting,
  } = useNotification(notification.id, { enabled: false });
  let primaryText;
  let secondaryText;

  if ('pgx-doctor-consent' === notification.type) {
    primaryText = `${ t('notificationsPage.pgx.request') } - ${ dateFormat(notification.created_at) }`;
    secondaryText = `${ t('notificationsPage.pgx.requestKit') }${ notification.metadata.serial_number } ${ t('notificationsPage.pgx.send') }`;
  } else if ('pgx-doctor-consent-accept' === notification.type) {
    primaryText = `${ t('notificationsPage.pgx.accept1') } - ${ dateFormat(notification.created_at) }`;
    secondaryText = `${ t('Ο Ιατρός έχει προβεί σε συγκατάθεση για την διεξαγωγή του τέστ Φαρμακογενετική ανάλυση CNS PGx kit με serial number') } ${ notification.metadata.serial_number }`;
  } else if ('pgx-doctor-consent-reject' === notification.type) {
    primaryText = `${ t('notificationsPage.pgx.reject1') } - ${ dateFormat(notification.created_at) }`;
    secondaryText = `${ t('Ο Ιατρός δεν έχει δώσει συγκατάθεση για την διεξαγωγή του τέστ Φαρμακογενετική ανάλυση CNS PGx kit με serial number') } ${ notification.metadata.serial_number }`;
  } else if ('healthcare-professional-share-results' === notification.type) {
    primaryText = `${ t('notificationsPage.healthcare.request') } - ${ dateFormat(notification.created_at) }`;
    secondaryText = `${ t('notificationsPage.healthcare.requestKit') } ${ notification.metadata.serial_number } ${ t('notificationsPage.healthcare.send') }`;
  } else if ('healthcare-professional-share-results-accept' === notification.type) {
    primaryText = `${ t('notificationsPage.healthcare.accept') } - ${ dateFormat(notification.created_at) }`;
    secondaryText = `${ t('Ο') } ${ t('doctorHealthProfessional') } ${ notification.metadata.from.family_name } ${ notification.metadata.from.name } ${ t('έχει αποδεχτεί την πρόσκληση για να αποκτήσει πρόσβαση στον προσωπικό σας φάκελο στην υπηρεσία myEMR της iDNA Genomics για το τεστ') } ${ notification.metadata.thematic_category } ${ t('notificationsPage.healthcare.doctorReportText1') } ${ notification.metadata.serial_number }`;
  } else if ('healthcare-professional-share-results-reject' === notification.type) {
    primaryText = `${ t('notificationsPage.healthcare.reject') } - ${ dateFormat(notification.created_at) }`;
    secondaryText = `${ t('Ο') } ${ t('doctorHealthProfessional') } ${ notification.metadata.from.family_name } ${ notification.metadata.from.name } ${ t('δεν αποδέχεται την πρόσκληση για να αποκτήσει πρόσβαση στον προσωπικό σας φάκελο στην υπηρεσία myEMR της iDNA Genomics για το τεστ') } ${ notification.metadata.thematic_category } ${ t('notificationsPage.healthcare.doctorReportText1') } ${ notification.metadata.serial_number }`;
  } else if ('sample-received' === notification.type) {
    primaryText = `${ t('Sample received') } - ${ dateFormat(notification.created_at) }`;
    secondaryText = `${ t('We inform you that we have received the buccal swab sample with name') } ${ notification.metadata.from.family_name } ${ notification.metadata.from.name } ${ t('and serial number') } ${ notification.metadata.serial_number }.`;
  } else if ('done' === notification.type) {
    primaryText = `${ t('Report available') } - ${ dateFormat(notification.created_at) }`;
    secondaryText = `${ t('We inform you that the results report for the serial number') } ${ notification.metadata.serial_number } ${ t('είναι διαθέσιμο στην επιλογή Αποτελέσματα.') }`;
  }

  return (
    <ListItem
      className={
        clsx(
          classes.listItem,
          i % 2 ? (
            notification.read ? classes.listItemEven : classes.listItemEvenActive
          ) : (notification.read ? classes.listItemOdd : classes.listItemOddActive),
        )
      }
    >
      <ListItemText
        primary={ primaryText }
        secondary={ secondaryText }
      />
      { !notification.read && (
        <IconButton
          style={ { color: theme.palette.success.main } }
          component='span'
          disabled={ notificationIsUpdating }
          onClick={ () => {
            return readNotification();
          } }
        >
          { notificationIsUpdating ? (
            <CircularProgress
              color='primary'
              variant='indeterminate'
              size={ 24 }
            />
          ) : <VisibilityIcon color='action' /> }
        </IconButton>
      ) }
      <IconButton
        style={ { color: theme.palette.error.main } }
        component='span'
        disabled={ notificationIsDeleting }
        onClick={ () => {
          return deleteNotification();
        } }
      >
        { notificationIsDeleting ? (
          <CircularProgress
            color='primary'
            variant='indeterminate'
            size={ 24 }
          />
        ) : <MdDelete /> }
      </IconButton>
    </ListItem>
  );
};

const NotificationsList = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [ onlyUnread, setOnlyUnread ] = useState(false);

  const {
    notifications,
    notificationsLoading,
    notificationsError,
    pagination,
    notificationsCount,
  } = useNotifications(onlyUnread);

  const filterLabel = useMemo(() => {
    let value = onlyUnread ? `${ t('unread') }` : `${ t('all') }`;
    if (notificationsCount) {
      value = `${ value } (${ notificationsCount })`;
    }
    return value;
  }, [ t, onlyUnread, notificationsCount ]);

  if (notificationsLoading) {
    return <AppLoading />;
  }

  if (notificationsError) {
    return <p>Failed to load notifications</p>;
  }

  return (
    <Grid container>
      <Grid container item xs={ 12 } justifyContent='space-between' alignItems='center'>
        <Grid item>
          <Button
            onClick={ () => { return setOnlyUnread((p) => { return !p; }); } }
            variant='outlined'
            startIcon={ <FilterIcon /> }
          >
            {filterLabel}
          </Button>
        </Grid>
        <Grid item>
          <AppPagination
            page={ pagination.page }
            setPage={ pagination.setPage }
            count={ pagination.count }
            limit={ pagination.limit }
          />
        </Grid>
      </Grid>
      <Grid container item xs={ 12 }>
        <List className={ classes.list }>
          {
            notifications.map((notification, i) => {
              return (
                <NotificationItem
                  key={ notification.id }
                  notification={ notification }
                  i={ i }
                />
              );
            })
          }
        </List>
      </Grid>
    </Grid>
  );
};

export default NotificationsList;
